import React, { FC, useEffect, useState } from "react";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import CommonLayout from "./CommonLayout";
import FormItem from "./FormItem";
import { useLocation, useNavigate } from "react-router-dom";
import Textarea from "shared/Textarea/Textarea";
import { useForm } from "react-hook-form";
import { useAppSelector, useAppDispatch } from "../../redux/hook";
import { updateFormData, selectFormData } from "../../redux/reducers/formReducer";

export interface PageCompanyRegisterProps {}

const PageCompanyRegister: FC<PageCompanyRegisterProps> = () => {
  const formData = useAppSelector(selectFormData);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => (state as any).user);
  
  const { register, handleSubmit, formState: { errors }, setValue } = useForm({
    defaultValues: {
      name: formData.company_name || '',
      description: formData.description || '',
      email: formData.company_email || '',
      website: formData.website || '',
      phone: formData.phone || ''
    }
  });
  const location = useLocation();
  const {state} = useLocation();
  const navigate = useNavigate();
  const companyType = location.pathname.indexOf('brand')>-1 ? 'brand' : 'agency';
  const nextHref = `/register/${companyType}/2`;

  const initialValues = {
    user_id: formData.user_id || state?.user?.id,
    company_type: companyType,
    name: '',
    description: '',
    business_type: formData.business_type || "GmbH",
    street_address: "",
    post: "",
    city: "",
    country: formData.country || "DE",
    email: "",
    website: "",
    phone: "",
    lat: 0,
    lng: 0,
  }
  const [inputValues, setInputValues] = useState(initialValues);

  useEffect(() => {
    if (!user || !user.id) {
      navigate('/register');
      return;
    }
    
    if (user.company && user.company.is_confirmed) {
      navigate('/');
      return;
    }
    
    if (user.company && !user.company.is_confirmed) {
      navigate('/register/thank-you');
      return;
    } 
  }, [user, navigate]);

  useEffect(() => {
    if (formData.company_name) setValue('name', formData.company_name);
    if (formData.description) setValue('description', formData.description);
    if (formData.company_email) setValue('email', formData.company_email);
    if (formData.website) setValue('website', formData.website);
    if (formData.phone) setValue('phone', formData.phone);
    if (formData.business_type) {
      setInputValues(prev => ({
        ...prev,
        business_type: formData.business_type
      }));
    }
  }, [formData, setValue]);

  useEffect(() => {
    const pathCompanyType = location.pathname.indexOf('brand') > -1 ? 'brand' : 'agency';
    const storedCompanyType = localStorage.getItem('company_type');
    
    if (storedCompanyType && storedCompanyType !== pathCompanyType) {
      navigate(`/register/${storedCompanyType}`);
    } else if (!storedCompanyType) {
      localStorage.setItem('company_type', pathCompanyType);
    }
  }, [user, navigate, location.pathname]);
  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    
    dispatch(updateFormData({ [name]: value }));
  };
  

  const handleCompanySubmit = (data) => {
    dispatch(updateFormData({
      company_name: data.name,
      description: data.description,
      company_email: data.email,
      website: data.website,
      phone: data.phone,
      business_type: inputValues.business_type
    }));
    
    const companyData = {
      user_id: inputValues.user_id,
      company_type: companyType,
      name: data.name,
      description: data.description,
      business_type: inputValues.business_type,
      email: data.email,
      website: data.website,
      phone: data.phone,
      lat: 0,
      lng: 0,
      country: inputValues.country,
      street_address: formData.street_address || "",
      post: formData.post || "",
      city: formData.city || "",
    };
    
    navigate(nextHref, { state: { input: companyData } });
  }
  

  return (
    <CommonLayout
      index="01"
      backtHref="/register"
      nextHref={nextHref}
      nextBtnText="Continue"
      onSubmit={handleSubmit(handleCompanySubmit)}
      hideBackBtn={true}
    >
      <>
        <h2 className="text-2xl font-semibold">Company Details</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        {/* FORM */}
        <div className="space-y-8">
          <FormItem
            label="Company name"
          >
            <Input 
              placeholder="Company name"
              name='name'
              {...register( "name", { required: 'Company Name field is required.' } )}
            />
            {errors.name && 
              <span className="ml-1 mt-2 text-xs text-red-500">
                {errors.name.message.toString()}
              </span>
            }
          </FormItem>
          {/* ITEM */}
          <FormItem
            label="Business Type"
          >
            <Select 
              name='business_type'
              value={inputValues?.business_type}
              onChange={handleInputChange}
            >
              <option value="GmbH">GmbH</option>
              <option value="KG">KG</option>
              <option value="OHG">OHG</option>
              <option value="GBR">GBR</option>
              <option value="UG ">UG</option>
              <option value="AG">AG</option>
            </Select>
          </FormItem>
          <FormItem
            label="Email"
          >
            <Input 
              placeholder="Email"
              name='email'
              {...register( "email", { 
                required: 'Email field is required.',
                pattern: {
                  value: /\S+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/,
                  message: "Please enter a valid email address"
                } 
              } )}
            />
            {errors.email && 
              <span className="ml-1 mt-2 text-xs text-red-500">
                {errors.email.message.toString()}
              </span>
            }
          </FormItem>
          <FormItem
            label="Website"
          >
            <Input 
              placeholder="Website"
              name='website'
              {...register( "website", { required: 'Website field is required.' } )}
            />
            {errors.website && 
              <span className="ml-1 mt-2 text-xs text-red-500">
                {errors.website.message.toString()}
              </span>
            }
          </FormItem>
          <FormItem
            label="Phone"
          >
            <Input 
              placeholder="Telefon"
              name='phone'
              {...register( "phone", { required: 'Phone field is required.' } )}
            />
            {errors.phone && 
              <span className="ml-1 mt-2 text-xs text-red-500">
                {errors.phone.message.toString()}
              </span>
            }
          </FormItem>
          <>
            <div>
              <h2 className="text-2xl font-semibold">
                About the Company
              </h2>
              <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
                Mention the best features of your product or service or the history of your company.
              </span>
            </div>

            <Textarea 
              rows={14}
              name='description'
              {...register( "description", { required: 'About Company field is required.' } )}
            />
            {errors.description && 
              <span className="ml-1 mt-2 text-xs text-red-500">
                {errors.description.message.toString()}
              </span>
            }
          </>
        </div>
      </>
    </CommonLayout>
  );
};

export default PageCompanyRegister;
